'use client'

/* eslint-disable @typescript-eslint/promise-function-async */

import { registerOverScreens, type OverScreenLoader } from './registerOverScreens'
import { type DialogIds, type FlyoutIds, type PopupIds, type WindowIds } from './types'

const overScreenLoaders = {
  popups: {
    apiKeyConnectPopup: () => import('src/dataSource/components/Popup/ApiKeyConnectPopup'),
    connectNewDataSourcePopup: () => import('src/dataSource/components/Popup/ConnectNewDataSourcePopup'),
    serviceAccountAccessPopup: () => import('src/dataSource/components/Popup/ServiceAccountAccessPopup'),
    userOrServiceAccountSelectionPopup: () =>
      import('src/dataSource/components/Popup/UserOrServiceAccountSelectionPopup'),
    accountSelectionPopup: () => import('src/dataSource/components/Popup/AccountSelectionPopup'),
    selectDataSourceCredentialsPopup: () => import('src/dataSource/components/Popup/SelectDataSourceCredentialsPopup'),
    unableToDeleteAccountPopup: () => import('src/profile/components/UnableToDeleteAccountPopup'),
    deleteAccountPopup: () => import('src/profile/components/DeleteProfilePopup'),
    createNewBudgetOptimiserPopup: () => import('src/budgetOptimiserV2/components/Popup/CreateNewBudgetOptimiserPopup'),
    returnCurvePopupV2: () => import('src/budgetOptimiserV2/components/ReturnCurveModal/ReturnCurvePopup'),
    inputSheetsLinkPopup: () => import('reportingSolution/components/Popup/InputSheetsLinkPopup'),
    createNewChannelGroupingPopup: () => import('src/channelGrouping/components/Popup/CreateNewChannelGroupingPopup'),
    dagViewStatusPopup: () => import('shared/components/DagViewStatusPopup'),
    recommendationsPopupV2: () => import('src/recommendations/components/RecommendationDetailsPopup'),
    editRecommendationPopup: () => import('src/recommendations/components/EditRecommendationPopup'),
    deletePopup: () => import('shared/components/DeletePopup'),
    deleteBulkPopup: () => import('shared/components/DeleteBulkPopup'),
    caseStatementBuilderPopup: () =>
      import('reportingSolution/components/CaseStatementBuilder/CaseStatementBuilderPopup'),
    selectThirdPartyProviderPlatformPopup: () =>
      import('src/dataSource/components/Popup/SelectThirdPartyProviderPlatformPopup'),
    thirdPartyPreparePopup: () => import('src/dataSource/components/Popup/ThirdPartyPreparePopup'),
    creativeReportingCreateDashboardPopup: () => import('src/creativeReporting/components/CreateDashboardPopup'),
    creativeReportingAdvancedDetailsPopup: () => import('src/creativeReporting/components/AdvancedDetailsPopup'),
    creativeReportingAdsDrilldownPopup: () => import('src/creativeReporting/components/Dashboard/AdsDrilldownPopup'),
    leaveWorkspacePopup: () => import('src/workspace/components/Popup/LeaveWorkspacePopup'),
    creativeAssetsCreateOrEditProjectPopup: () =>
      import('src/creativeAssets/components/ProjectPage/CreateOrEditProjectPopup'),
    attributionChannelResultsPopup: () => import('src/attributionModelV2/components/ChannelResultsPopup'),
    biddingSignalsEventDagViewPopup: () => import('src/biddingSignals/components/EventDagViewPopup'),
    creativeAssetsUploadPopup: () => import('src/creativeAssets/components/ProjectPage/UploadPopup'),
    creativeAssetsBulkStatusUpdatePopup: () =>
      import('src/creativeAssets/components/ProjectPage/AdLibraryTab/BulkStatusUpdatePopup'),
    copilotKnowledgeDocumentPopup: () => import('src/copilot/components/KnowledgeDocumentPopup')
  },
  flyouts: {
    budgetOptimiserScenariosFlyout: () => import('src/budgetOptimiserV2/components/ScenarioFlyout'),
    recommendedActionsFlyout: () => import('src/budgetOptimiserV2/components/RecommendedActions'),
    budgetOptimiserSwitchConfigFlyout: () => import('src/budgetOptimiserV2/components/SwitchConfigFlyout'),
    profileFlyout: () => import('src/profile/components/Flyout'),
    switchReportHomeFlyout: () => import('src/home/components/SwitchReportFlyout'),
    attributionFilterFlyout: () =>
      import('src/attributionModelV2/components/EditOrCreateModel/AccountStep/FilterFlyout'),
    attributionCustomDataSourcesFlyout: () =>
      import('src/attributionModelV2/components/EditOrCreateModel/AccountStep/CustomDataSourcesFlyout'),
    globalFilterFlyout: () => import('shared/components/Filter/FilterFlyout'),
    topCardMetricsFlyout: () => import('src/home/components/TopCardMetricsFlyout'),
    conversionEventsFlyout: () => import('shared/components/ConversionEventsFlyout'),
    budgetAndTargetsFlyout: () => import('src/home/components/BudgetAndTargetsFlyout'),
    creativeReportingEditTopViewFlyout: () => import('src/creativeReporting/components/TopViewFlyout'),
    switchCreativeReportFlyout: () => import('src/creativeReporting/components/SwitchReportFlyout'),
    switchCreativeDashboardFlyout: () => import('src/creativeReporting/components/Dashboard/SwitchDashboardFlyout'),
    attributionModelsSelectorFlyout: () =>
      import('reportingSolution/components/EditOrCreatePage/OtherSettingsStep/AttributionModelsSelector/Flyout'),
    creativeAssetsAddCreativesToRequestChangesFlyout: () =>
      import('src/creativeAssets/components/ProjectAssetPage/AddCreativesToRequestChangesFlyout'),
    tagsFlyout: () => import('shared/components/TagsFlyout'),
    copilotKnowledgeSourceFlyout: () => import('src/copilot/components/KnowledgeSourceFlyout')
  },
  dialogs: {
    confirmUserDefinedUpdateDialog: () => import('src/connectors/components/ConfirmUserDefinedUpdateDialog'),
    confirmRunFullRefreshDialog: () => import('src/connectors/components/ConfirmRunFullRefreshDialog'),
    dashboardCreatedSuccessfullyHelperDialog: () =>
      import('reportingSolution/components/Popup/DashboardCreatedSuccessfullyHelperDialog'),
    dashboardCreateLeaveAlvieDialog: () => import('reportingSolution/components/Popup/DashboardCreateLeaveAlvieDialog')
  },
  windows: {
    oauthWindow: () => import('src/dataSource/components/OauthWindow'),
    thirdPartyWindow: () => import('src/dataSource/components/ThirdPartyWindow')
  }
} as const satisfies {
  popups: Record<PopupIds, OverScreenLoader>
  flyouts: Record<FlyoutIds, OverScreenLoader>
  dialogs: Record<DialogIds, OverScreenLoader>
  windows: Record<WindowIds, OverScreenLoader>
}

registerOverScreens({
  ...overScreenLoaders.popups,
  ...overScreenLoaders.flyouts,
  ...overScreenLoaders.dialogs,
  ...overScreenLoaders.windows
})

/* eslint-enable @typescript-eslint/promise-function-async */
